import React from 'react';
import { mergeStyleSets } from '@fluentui/react';
import ErrorMessageComponent from '../error-message/error-message.component';
import moment from 'moment';

import ItemBandejaEntrada from '../../models/ItemBandejaEntrada';
import ItemBandejaEntradaActuacion from '../../models/ItemBandejaEntradaActuacion';

const listClasses = mergeStyleSets({
    orderCard: {
        backgroundColor: '#ffffff',
        borderRadius: 10,
        marginBottom: 10,
        boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
        overflow: 'hidden',
    },
    orderHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        borderBottom: '1px solid #f0f0f0',
        gap: 8,
    },
    orderNumber: {
        color: 'var(--color-primario)',
        fontWeight: 700,
        fontSize: 13,
        flexShrink: 0,
    },
    orderContact: {
        fontSize: 12,
        color: '#555',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1,
    },
    actuacionRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        gap: 10,
        cursor: 'pointer',
        borderBottom: '1px solid #f8f8f8',
        transition: 'background 0.15s',
        selectors: {
            ':last-child': {
                borderBottom: 'none',
            },
            ':hover': {
                backgroundColor: '#FFF3F2',
            },
        },
    },
    actuacionRowSelected: {
        backgroundColor: 'var(--color-primario) !important',
        selectors: {
            '& *': {
                color: 'white !important',
            },
        },
    },
    iconBadge: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: 'var(--color-primario)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
    },
    iconBadgeText: {
        fontSize: 12,
        color: 'white',
        fontWeight: 700,
    },
    actuacionContent: {
        flex: 1,
        minWidth: 0,
    },
    actuacionRef: {
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--color-primario)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    actuacionAddress: {
        fontSize: 11,
        color: '#777',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    actuacionDesc: {
        fontSize: 11,
        color: '#aaa',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    dateCol: {
        flexShrink: 0,
        textAlign: 'center',
        minWidth: 42,
    },
    dateDow: {
        fontSize: 9,
        fontWeight: 600,
        color: 'var(--color-primario)',
        textTransform: 'uppercase',
        display: 'block',
    },
    dateDay: {
        fontSize: 20,
        fontWeight: 700,
        color: 'var(--color-primario)',
        lineHeight: '1',
        display: 'block',
    },
    dateTime: {
        fontSize: 9,
        color: '#aaa',
        display: 'block',
    },
});

const dayOfWeekName = (dayOfWeek: number): string => {
    switch (dayOfWeek) {
        case 0: return "DOM";
        case 1: return "LUN";
        case 2: return "MAR";
        case 3: return "MIE";
        case 4: return "JUE";
        case 5: return "VIE";
        case 6: return "SAB";
        default: return "";
    }
};

interface IWorkOrderReadonlyListComponentProps {
    onSelectWork: (value: ItemBandejaEntradaActuacion, workOrder: ItemBandejaEntrada) => void;
    workOrders: ItemBandejaEntrada[];
    selectedWorkOrder?: ItemBandejaEntrada;
    selectedWork?: ItemBandejaEntradaActuacion;
};

const WorkOrderReadonlyListComponent = (props: IWorkOrderReadonlyListComponentProps) => {
    return (
        <>
            {
                (props.workOrders.length === 0) ? (
                    <ErrorMessageComponent message='No hay datos para mostrar' />
                ) : (
                    props.workOrders.map((wo: ItemBandejaEntrada, i: number) => (
                        <div key={i} className={listClasses.orderCard}>
                            <div className={listClasses.orderHeader}>
                                <span className={listClasses.orderNumber}>{wo.NumeroDocumento}</span>
                                <span className={listClasses.orderContact}>{wo.NombreContacto}</span>
                            </div>
                            {wo.Actuaciones.map((actuacion: ItemBandejaEntradaActuacion, j: number) => {
                                const isSelected = wo.IdOrden === props.selectedWorkOrder?.IdOrden && actuacion.IdTarea === props.selectedWork?.IdTarea;
                                const fecha = moment(actuacion.FechaHoraInicioPrevista);
                                return (
                                    <div
                                        key={j}
                                        className={`${listClasses.actuacionRow} ${isSelected ? listClasses.actuacionRowSelected : ''}`}
                                        onClick={() => props.onSelectWork(actuacion, wo)}>
                                        <div className={listClasses.iconBadge}>
                                            {actuacion.IconoEstadoBase64 ? (
                                                <img
                                                    src={actuacion.IconoEstadoBase64}
                                                    alt="Estado"
                                                    height={20}
                                                    width={20} />
                                            ) : (
                                                <span className={listClasses.iconBadgeText}>
                                                    {(actuacion.Referencia || actuacion.IdTarea.toString()).substring(0, 2)}
                                                </span>
                                            )}
                                        </div>
                                        <div className={listClasses.actuacionContent}>
                                            <div className={listClasses.actuacionRef}>
                                                {actuacion.Referencia ? actuacion.Referencia : `#${actuacion.IdTarea}`}
                                            </div>
                                            {actuacion.DireccionCompleta && (
                                                <div className={listClasses.actuacionAddress}>
                                                    {actuacion.DireccionCompleta}
                                                </div>
                                            )}
                                            {actuacion.Descripcion && (
                                                <div
                                                    className={listClasses.actuacionDesc}
                                                    dangerouslySetInnerHTML={{ __html: actuacion.Descripcion }} />
                                            )}
                                        </div>
                                        <div className={listClasses.dateCol}>
                                            <span className={listClasses.dateDow}>
                                                {dayOfWeekName(fecha.day())}
                                            </span>
                                            <span className={listClasses.dateDay}>
                                                {fecha.format('DD')}
                                            </span>
                                            <span className={listClasses.dateTime}>
                                                {fecha.format('HH:mm')}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))
                )
            }
        </>
    );
};

export default WorkOrderReadonlyListComponent;
